import { Tooltip } from '@app/components/tooltip';
import cx from 'classnames';
import React from 'react';

import { CreatorAvatarTemplate } from '../../events/creator-avatar-template';
import {
  TOrganizerEventTypeDistribution,
  TOrganizerStatus,
} from '../hooks/use-events-by-organizer';

const STATUS_TEXT_COLOR: Record<TOrganizerStatus, string> = {
  active: 'text-on-site-text',
  inviteSent: 'text-webinar-text',
  deleted: 'text-gray-400',
  rejected: 'text-red-500',
};

type TEventTypeBadgeConfig = {
  key: keyof TOrganizerEventTypeDistribution;
  colorClasses: string;
};

const EVENT_TYPE_BADGE_CONFIG: TEventTypeBadgeConfig[] = [
  { key: 'webinar', colorClasses: 'bg-webinar-bg text-webinar-text' },
  { key: 'virtual', colorClasses: 'bg-virtual-bg text-virtual-text' },
  { key: 'hybrid', colorClasses: 'bg-hybrid-bg text-hybrid-text' },
  { key: 'onSite', colorClasses: 'bg-on-site-bg text-on-site-text' },
];

export type OrganizerRowProps = {
  name: string;
  email: string;
  pictureUrl: string | null;
  status: TOrganizerStatus;
  statusLabel: string;
  seatAllocationDate: string | null;
  recentEventDate: string | null;
  eventsHosted: number;
  eventsCreated: number;
  eventTypeDistribution: TOrganizerEventTypeDistribution;
  eventTypeLabels: Record<keyof TOrganizerEventTypeDistribution, string>;
  rowTestId: string;
  statusTestId: string;
};

export const OrganizerRow = ({
  name,
  email,
  pictureUrl,
  status,
  statusLabel,
  seatAllocationDate,
  recentEventDate,
  eventsHosted,
  eventsCreated,
  eventTypeDistribution,
  eventTypeLabels,
  rowTestId,
  statusTestId,
}: OrganizerRowProps) => (
  <tr
    className="border-b border-neutral-b4 last:border-b-0"
    data-testid={rowTestId}
  >
    <td className="p-2 align-middle">
      <div className="flex items-center">
        <CreatorAvatarTemplate
          data={{ creatorEmail: email, pictureUrl }}
          addTooltip={false}
        />
      </div>
    </td>
    <td className="p-2 align-middle">
      <Tooltip tooltip={name} placement="bottom-center">
        <span className="block truncate text-sm text-gray-800">{name}</span>
      </Tooltip>
    </td>

    <td className="p-2 align-middle">
      <Tooltip tooltip={email} placement="bottom-center">
        <span className="block truncate text-subtitle text-gray-400">
          {email}
        </span>
      </Tooltip>
    </td>

    <td className="p-2 align-middle">
      <span
        className={cx('text-subtitle font-medium', STATUS_TEXT_COLOR[status])}
        data-testid={statusTestId}
      >
        {statusLabel}
      </span>
    </td>
    <td className="p-2 align-middle">
      <span className="text-subtitle text-gray-400">
        {seatAllocationDate || '-'}
      </span>
    </td>

    <td className="p-2 align-middle">
      <span className="text-subtitle text-gray-800">{eventsCreated}</span>
    </td>

    <td className="p-2 align-middle">
      <span className="text-subtitle text-gray-800">{eventsHosted}</span>
    </td>

    <td className="p-2 align-middle">
      <span className="text-subtitle text-gray-400">
        {recentEventDate || '-'}
      </span>
    </td>

    <td className="p-2 align-middle">
      <div className="flex flex-wrap gap-1">
        {EVENT_TYPE_BADGE_CONFIG.map(({ key, colorClasses }) => {
          const count = eventTypeDistribution[key];
          if (!count) return null;
          return (
            <span
              key={key}
              className={cx(
                'inline-flex items-center rounded px-1 py-0.5 text-descriptor-mini font-medium',
                colorClasses,
              )}
            >
              {count} {eventTypeLabels[key]}
            </span>
          );
        })}
      </div>
    </td>
  </tr>
);
